import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Alert, CircularProgress, Card, CardContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { PageHeader } from '../../components/ui/PageHeader';
import { BiltyPrint, printBilty } from '../../components/bilty/BiltyPrint';
import { biltyAPI, invoiceAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { RADIUS, brandCss } from '../../theme/appTheme';

export const BiltyDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCurrency, formatDate } = useLocale();
  const [loading, setLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState('');
  const [bilty, setBilty] = useState<Record<string, any> | null>(null);
  const [company, setCompany] = useState<Record<string, any> | null>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<{ _id: string; invoiceNumber?: string } | null>(null);

  const canEdit = hasPermission('bilty', 'edit');
  const canPrint = hasPermission('bilty', 'print') || hasPermission('bilty', 'view');
  const canCreateInvoice = hasPermission('invoices', 'create');
  const canViewInvoice = hasPermission('invoices', 'view');

  const load = () => {
    if (!id) return;
    setLoading(true);
    biltyAPI.getById(id).then((res) => {
      setBilty(res.data.bilty);
      setCompany(res.data.company);
      setLinkedInvoice(res.data.invoice || null);
      if (searchParams.get('print') === '1') {
        setTimeout(() => printBilty(res.data.bilty, res.data.company, { formatCurrency, formatDate }), 500);
      }
    }).catch(() => setError('Failed to load bilty')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, searchParams]);

  const handleCreateInvoice = async () => {
    if (!id) return;
    setInvoiceLoading(true);
    setError('');
    try {
      const res = await invoiceAPI.createFromBilty(id);
      const invoiceId = res.data.invoice?._id;
      showToast('Invoice created from bilty');
      navigate(`/invoices/${invoiceId}?print=1`);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { invoiceId?: string; message?: string } } };
      if (e.response?.status === 409 && e.response.data?.invoiceId) {
        showToast('Invoice already exists for this bilty');
        navigate(`/invoices/${e.response.data.invoiceId}`);
        return;
      }
      setError(e.response?.data?.message || 'Failed to create invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!bilty) {
    return <Alert severity="error">Bilty not found</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title={`Bilty ${bilty.biltyNumber}`}
        subtitle={`${bilty.source} → ${bilty.destination} · FY ${bilty.fiscalYear || '—'}`}

        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/bilty')}>Back</Button>
            {canEdit && (
              <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`/bilty/${id}/edit`)}>
                Edit
              </Button>
            )}
            {linkedInvoice && canViewInvoice ? (
              <Button
                variant="contained"
                startIcon={<ReceiptLongIcon />}
                onClick={() => navigate(`/invoices/${linkedInvoice._id}`)}
                sx={{ background: brandCss.gradientPrimary }}
              >
                View Invoice {linkedInvoice.invoiceNumber ? `(${linkedInvoice.invoiceNumber})` : ''}
              </Button>
            ) : canCreateInvoice ? (
              <Button
                variant="contained"
                startIcon={<ReceiptLongIcon />}
                disabled={invoiceLoading}
                onClick={handleCreateInvoice}
                sx={{ background: brandCss.gradientPrimary }}
              >
                Create Invoice
              </Button>
            ) : null}
            {canPrint && (
              <Button
                variant="contained"
                startIcon={<PrintOutlinedIcon />}
                onClick={() => printBilty(bilty, company, { formatCurrency, formatDate })}
                sx={{ background: brandCss.gradientPrimary }}
              >
                Print
              </Button>
            )}
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!linkedInvoice && canCreateInvoice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Create an invoice from this bilty to print the transport LR format with invoice number and amounts.
        </Alert>
      )}

      <Card sx={{ borderRadius: `${RADIUS}px` }}>
        <CardContent>
          <BiltyPrint bilty={bilty} company={company} />
        </CardContent>
      </Card>
    </Box>
  );
};
